"use client";

import { TypeHero } from "@contentful/types";
import { customImageLoader, navigateCtaUrl } from "@yardzen/next-client-util";
import { Button } from "@yardzen/ui/components/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@yardzen/ui/components/carousel";
import { H1 } from "@yardzen/ui/components/h1";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { cn } from "@yardzen/ui/lib/utils";
import AutoScroll from "embla-carousel-auto-scroll";
import Image from "next/image";
import { useRouter } from "next/navigation";

export function PhotoSliderCarousel({
  hero,
  sectionName,
  titleClassName,
}: {
  hero: TypeHero<"WITHOUT_UNRESOLVABLE_LINKS">;
  sectionName: string;
  titleClassName?: string;
}) {
  const router = useRouter();
  const { title, subtitle, images, displayOptions } = hero.fields;
  let carouselImages = images;
  if (carouselImages && carouselImages.length <= 6) {
    carouselImages = [...carouselImages, ...carouselImages];
  }

  const cta = hero.fields.cta;

  return (
    <div className="relative">
      <div className="mb-10 flex flex-col items-center text-center">
        {title && !displayOptions?.includes("suppress-title") && (
          <H1
            className={cn(
              "text-scheme-text-primary px-4 pt-12 text-3xl md:text-4xl lg:text-5xl",
              titleClassName,
            )}
          >
            {title}
          </H1>
        )}
        <P className="text-scheme-text-secondary mt-4 w-[90%] pb-8 text-base md:min-w-[37%] md:max-w-[65%] md:text-lg">
          {subtitle}
        </P>
        <Carousel
          plugins={[
            AutoScroll({
              playOnInit: true,
              stopOnInteraction: false,
              speed: 0.5,
            }),
          ]}
          opts={{
            loop: true,
          }}
        >
          <CarouselContent className="-ml-1" aria-live="off">
            {carouselImages?.map((image, index) => {
              const imageUrl = image?.fields?.image?.fields?.file?.url;
              const alt = image?.fields?.image?.fields?.description;
              if (!imageUrl) {
                return null;
              }
              return (
                <CarouselItem
                  key={index}
                  className="basis-4/5 sm:basis-1/3 md:basis-1/3 lg:basis-1/4"
                >
                  <Link
                    variant="nav"
                    onClick={() => {
                      navigateCtaUrl(hero.fields.cta?.fields.url, router);
                    }}
                  >
                    <Image
                      src={`https:${imageUrl}`}
                      alt={alt ?? ""}
                      height={500}
                      width={400}
                      className="h-full w-[28rem] rounded-[0.68981rem] object-cover"
                      loading="lazy"
                      loader={customImageLoader}
                      sizes="(max-width: 640px) 80vw,(max-width: 1024px) 33vw, 25vw"
                    />
                  </Link>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
        {cta && cta.fields.buttonText && cta.fields.url && (
          <div className="mt-10 flex flex-row justify-center">
            <Button
              variant={cta.fields.variant}
              className="w-full sm:w-auto"
              onClick={() => {
                navigateCtaUrl(cta?.fields.url, router);
              }}
            >
              {cta?.fields.buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
